import React, { useState, useEffect, useCallback, useRef } from "react";
import api from "../../api";

/**
 * SnapTradeLinkButton - Opens SnapTrade's hosted connection portal in a popup.
 *
 * On success, calls onSuccess({ authorizationId }) — the caller is responsible
 * for fetching the account list and creating/linking WealthPort accounts.
 *
 * Security: postMessage origin is validated against
 * REACT_APP_SNAPTRADE_PORTAL_ORIGIN before any message is processed.
 *
 * @param {function} onSuccess   - Called with { authorizationId } on success
 * @param {function} onError     - Called on error
 * @param {string}   buttonText  - Custom button text (optional)
 * @param {string}   className   - Custom Tailwind classes (optional)
 * @param {string}   reconnectId - Existing brokerage auth ID to reconnect (optional)
 */
const SnapTradeLinkButton = ({
  onSuccess,
  onError,
  buttonText = "Connect with SnapTrade",
  className = "",
  reconnectId,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const popupRef = useRef(null);
  const messageHandlerRef = useRef(null);

  const expectedOrigin =
    process.env.REACT_APP_SNAPTRADE_PORTAL_ORIGIN || "https://app.snaptrade.com";

  useEffect(() => {
    return () => {
      if (popupRef.current && !popupRef.current.closed) {
        popupRef.current.close();
      }
      if (messageHandlerRef.current) {
        window.removeEventListener("message", messageHandlerRef.current);
      }
    };
  }, []);

  const handleConnect = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // 1. Get connection URL from backend
      const response = await api.post("/snaptrade/create-connection-url", {
        ...(reconnectId ? { reconnect_id: reconnectId } : {}),
      });

      const redirectUri = response.data.redirect_uri;
      if (!redirectUri) {
        throw new Error("No redirect_uri returned from backend");
      }

      // 2. Open portal in a popup
      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      const popup = window.open(
        redirectUri,
        "snaptrade-connect",
        `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
      );

      if (!popup) {
        throw new Error(
          "Popup blocked. Please allow popups for this site and try again."
        );
      }

      popupRef.current = popup;

      // 3. Listen for postMessage from the portal
      const handleMessage = async (event) => {
        if (event.origin !== expectedOrigin) return;

        const data = event.data || {};
        const status = data.status || data.type || data.eventType;

        if (status === "SUCCESS" || status === "CLOSE_SUCCESS") {
          window.removeEventListener("message", handleMessage);
          messageHandlerRef.current = null;

          if (popup && !popup.closed) popup.close();

          const authorizationId =
            data.authorizationId ||
            data.brokerageAuthorizationId ||
            data.accountId ||
            data.id;

          setLoading(false);

          if (onSuccess) {
            onSuccess({ authorizationId });
          }
        } else if (
          status === "ERROR" ||
          status === "CLOSE_ERROR" ||
          status === "ABANDONED"
        ) {
          window.removeEventListener("message", handleMessage);
          messageHandlerRef.current = null;

          if (popup && !popup.closed) popup.close();

          const errMsg =
            data.errorMessage || data.message || "Connection cancelled or failed";
          setError(errMsg);
          setLoading(false);

          if (status !== "ABANDONED" && onError) {
            onError(new Error(errMsg));
          }
        }
      };

      messageHandlerRef.current = handleMessage;
      window.addEventListener("message", handleMessage);

      // Poll for manual popup close
      const pollClosed = setInterval(() => {
        if (popup.closed) {
          clearInterval(pollClosed);
          if (messageHandlerRef.current) {
            window.removeEventListener("message", messageHandlerRef.current);
            messageHandlerRef.current = null;
          }
          setLoading(false);
        }
      }, 500);
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || "Failed to connect";
      setError(detail);
      setLoading(false);
      if (onError) onError(err);
    }
  }, [reconnectId, expectedOrigin, onSuccess, onError]);

  const defaultClassName =
    "px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors";

  return (
    <div>
      <button
        onClick={handleConnect}
        disabled={loading}
        className={className || defaultClassName}
      >
        {loading ? "Connecting..." : buttonText}
      </button>

      {error && (
        <p className="mt-2 text-sm text-red-600 dark:text-red-400">{error}</p>
      )}
    </div>
  );
};

export default SnapTradeLinkButton;
